import { useEffect, useState } from "react";
import { ExternalLink, HardDrive } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export function ClienteDrivePanel({ cliente, onChanged }) {
  const [canManage, setCanManage] = useState(false);
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState(null);
  const [value, setValue] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    api.get("/integracoes/google/status").then(({ data }) => { if (active) setCanManage(data.conectado && data.pode_consultar); }).catch(() => {});
    return () => { active = false; };
  }, []);
  const base = `/clientes/${cliente.id}/drive-folder`;
  const destination = cliente.tipo === "recorrente" ? "0.0 RECORRENTES ATIVOS" : "0.1 ONETIME ATIVO";
  async function loadOptions(page = "") {
    setBusy(true); setError("");
    try {
      const { data } = await api.get(`${base}s`, { params: { pagina: page } });
      setOptions((old) => ({ ...data, pastas: page ? [...(old?.pastas || []), ...data.pastas] : data.pastas }));
    } catch (err) { setError(apiError(err)); }
    finally { setBusy(false); }
  }
  async function inspect() {
    setBusy(true); setError("");
    try { setPreview((await api.post(`${base}/preview`, { pasta: value })).data); }
    catch (err) { setError(apiError(err)); }
    finally { setBusy(false); }
  }
  async function save(create = false) {
    setBusy(true); setError("");
    try {
      if (create) await api.post(base);
      else await api.put(base, { pasta: preview.id, link_anterior: cliente.drive_link || "" });
      toast.success(create ? "Pasta criada no Drive compartilhado" : "Pasta vinculada ao cliente");
      setOpen(false); onChanged();
    } catch (err) { setError(apiError(err)); }
    finally { setBusy(false); }
  }
  return <section className="studio-card p-5 space-y-3" data-testid="cliente-drive-panel">
    <p className="caption-label flex gap-2 items-center"><HardDrive size={16} /> Google Drive da empresa</p>
    {cliente.drive_link && <a href={cliente.drive_link} target="_blank" rel="noreferrer" data-testid="cliente-drive-link" className="flex items-center gap-2 text-sm text-[#8CC63F] break-all"><ExternalLink size={16} className="shrink-0" />{cliente.drive_folder_name || "Pasta no Google Drive"}</a>}
    <p className="text-sm text-[#A1A1AA]">Novas pastas: Netzer / {destination}</p>
    {canManage && cliente.status !== "encerrado" ? <Button variant="outline" className="w-full sm:w-auto" onClick={() => { setOpen(true); setPreview(null); setValue(""); loadOptions(); }}>
      {cliente.drive_link ? "Trocar ou vincular pasta existente" : "Criar ou vincular pasta"}
    </Button> : !cliente.drive_link && <p className="text-sm text-[#A1A1AA]">O administrador responsável pela conexão Google pode vincular a pasta.</p>}
    <Dialog open={open} onOpenChange={(v) => { if (!busy) setOpen(v); }}>
      <DialogContent className="bg-[#141418] border-[#27272A] text-[#F4F4F0] max-h-[90dvh] overflow-y-auto">
        <DialogHeader><DialogTitle>Pasta de {cliente.empresa}</DialogTitle><DialogDescription>Vincular altera somente o atalho no sistema. Não move, apaga nem muda as permissões de arquivos.</DialogDescription></DialogHeader>
        <p className="text-sm">Destino: {options?.destino?.nome || destination}</p>
        {error && <p role="alert" className="text-sm text-[#F43F5E]">{error}</p>}
        {options && <div className="space-y-2">
          <Label htmlFor="drive-existing">Escolher uma pasta existente</Label>
          <select id="drive-existing" className="w-full rounded-lg border border-[#27272A] bg-[#18181C] p-3" value={options.pastas.some(p => p.id === value) ? value : ""} disabled={busy} onChange={e => { setValue(e.target.value); setPreview(null); }}>
            <option value="">Selecione a pasta do cliente</option>
            {options.pastas.map(p => <option key={p.id} value={p.id}>{p.nome}</option>)}
          </select>
          {options.proxima_pagina && <Button variant="ghost" disabled={busy} onClick={() => loadOptions(options.proxima_pagina)}>Carregar mais pastas</Button>}
        </div>}
        <div className="space-y-2"><Label htmlFor="drive-folder-url">Ou cole o link da pasta</Label><Input id="drive-folder-url" value={value} disabled={busy} onChange={e => { setValue(e.target.value); setPreview(null); }} placeholder="https://drive.google.com/drive/folders/…" /></div>
        {!preview ? <Button disabled={busy || !value.trim()} onClick={inspect}>{busy ? "Consultando…" : "Conferir pasta"}</Button> : <div className="rounded-xl border border-[#8CC63F]/40 p-4 space-y-3">
          <p className="font-semibold">{preview.nome}</p><p className="text-sm">{preview.destino}</p>
          <a href={preview.link} target="_blank" rel="noreferrer" className="text-[#8CC63F] text-sm">Abrir e conferir no Drive</a>
          {cliente.drive_link && <p className="text-sm">O atalho atual será substituído. A pasta anterior continuará intacta.</p>}
          <Button className="w-full" disabled={busy} onClick={() => save()}>{busy ? "Vinculando…" : "Confirmar vínculo"}</Button>
        </div>}
        {!cliente.drive_link && options && <div className="border-t border-[#27272A] pt-4 space-y-2"><p className="text-sm text-[#A1A1AA]">Não existe uma pasta para este cliente? Confira a lista antes de criar outra.</p><Button variant="outline" className="w-full" disabled={busy} onClick={() => save(true)}>Criar nova pasta em {options.destino.nome}</Button></div>}
      </DialogContent>
    </Dialog>
  </section>;
}
